import type { ElementType } from 'react'

import { type AppModuleConfig } from '@webapp/app/config/useModuleConfig'
import { type FeatureFlags } from '@webapp/core/services/FeatureFlag'
import { type useBillsPermissions } from '@webapp/hooks/useBillsPermissions'
import {
  type FeatureSwitches,
  type NavigationPermissions,
  type SetupProgress,
} from '@webapp/models'
import { type Currency } from '@webapp/models/Currency'
import { type Enum } from '@webapp/utils'

export const TagTypes = {
  BETA: 'beta',
  NEW: 'new',
} as const

export type TagType = 'beta' | 'new'

export const NavigationSections = {
  HOME: 'dashboard',
  APPROVE: 'approve',
  EXPENSE: 'expense',
  PURCHASE: 'purchase',
  SPEND: 'spend',
  ANALYTICS: 'analytics',
  SETTINGS: 'settings',
  /**
   * Unlike the other sections, this value is only the `INavigationItems` key —
   * the nav item's `id` is `NavigationHeaders.DISCOVER_MORE`.
   */
  DISCOVER_MORE: 'discoverMore',
} as const
export type NavigationSection = Enum<typeof NavigationSections>

export const NavigationHeaders = {
  HOME: 'dashboard',
  APPROVE: 'approve',
  EXPENSE_REQUESTS: 'request__expense',
  TRAVEL_REQUESTS: 'request__travel',
  SPENDING_CARD: 'spending_card',
  ORDER_REQUESTS: 'request__order',
  PURCHASE_AGREEMENT_REQUESTS: 'request__purchase_agreement',
  PROCURE: 'procure',
  RECEIVE: 'receive',
  FINANCIAL_ACCOUNT: 'ap__payment__financial_account',
  BILL_PAY_PROMO: 'ap__payment__discover_bill_pay',
  PAYMENT_LEGACY: 'payment',
  ACCOUNTS_PAYABLE: 'ap',
  AP_PROMO: 'discover_ap',
  REPORTS: 'reports',
  SPEND_DASHBOARD: 'data_and_insights__spend_insights',
  DISCOVER_MORE: 'discover_more',
} as const
export type NavigationHeader = Enum<typeof NavigationHeaders>

export const NavigationSubheaders = {
  AP_BILLS: 'bills',
  AP_CREDIT_CARDS: 'credit_cards',
  AP_PAYMENT_LEGACY: 'payment',
  AP_PAYMENT: 'payments',
  AP_SPEND_REPORTS: 'spend',
  PURCHASE_REPORTS: 'purchase_reports',
  EXPENSE_REPORTS: 'expense_reports',
} as const
export type NavigationSubheader = Enum<typeof NavigationSubheaders>

export interface INavigationSub {
  title?: string
  url?: string
  notificationKey?: string
  legacy?: boolean
  active?: boolean
  id?: NavigationSubheader | NavigationPermissions
  isSubheader?: boolean
  links?: NavigationPermissions[]
  condition?: (context: {
    featureFlags: Partial<FeatureFlags> & Partial<FeatureSwitches>
    setupProgress: SetupProgress
    entitlement: Partial<AppModuleConfig['navigation']> // maybe some kind of key?
    billsPermissions: ReturnType<typeof useBillsPermissions>
    isSuperuser?: boolean
    baseCurrency?: Currency
  }) => boolean
  matchUrls?: string[]
  exactMatch?: boolean
  tag?: TagType
}

export interface INavigationItem {
  title?: string
  icon?: ElementType
  tag?: TagType
  url?: string
  legacy?: boolean
  notificationKey?: string
  children?: INavigationSub[]
  id?: NavigationHeader | NavigationPermissions
  condition?: (context: {
    featureFlags: Partial<FeatureFlags> & Partial<FeatureSwitches>
    setupProgress: SetupProgress
    entitlement: Partial<AppModuleConfig['navigation']>
    billsPermissions: ReturnType<typeof useBillsPermissions>
    isSuperuser?: boolean
    baseCurrency?: Currency
  }) => boolean
  matchUrls?: string[]
  exactMatch?: boolean
}
